import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { faCheckSquare } from '@fortawesome/pro-regular-svg-icons/faCheckSquare';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { faSquare } from '@fortawesome/pro-regular-svg-icons/faSquare';
import { powerSchoolContacts } from 'ducks';
import Placeholder from 'components/Placeholder';
import { duckify, singleDuck } from 'utils/duckify';
import FA from 'utils/FA';
import './PowerSchoolContacts.scss';
import LinkButton from 'components/LinkButton/LinkButton';

const PowerSchoolContactType = PropTypes.shape({
  contactId: PropTypes.number,
  firstName: PropTypes.string,
  middleName: PropTypes.string,
  lastName: PropTypes.string,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  gender: PropTypes.string,
  emails: PropTypes.arrayOf(PropTypes.shape({
    contactEmailId: PropTypes.number,
    emailId: PropTypes.number,
    type: PropTypes.string,
    address: PropTypes.string
  })),
  phones: PropTypes.arrayOf(PropTypes.shape({
    contactsPhoneId: PropTypes.number,
    phoneNumberId: PropTypes.number,
    phoneType: PropTypes.string,
    phoneNumber: PropTypes.string
  })),
  addresses: PropTypes.arrayOf(PropTypes.shape({
    contactsAddressId: PropTypes.number,
    addressId: PropTypes.number,
    addressType: PropTypes.string,
    street: PropTypes.string,
    linetwo: PropTypes.string,
    unit: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    country: PropTypes.string,
    postalcode: PropTypes.string
  })),
  contactStudents: PropTypes.arrayOf(PropTypes.shape({
    studentContactId: PropTypes.number,
    dcid: PropTypes.number,
    originalContactType: PropTypes.string,
    studentDetails: PropTypes.arrayOf(PropTypes.shape({
      studentContactDetailId: PropTypes.number,
      relationship: PropTypes.string,
      custodial: PropTypes.bool,
      emergency: PropTypes.bool,
      livesWith: PropTypes.bool,
      schoolPickup: PropTypes.bool,
      receivesMail: PropTypes.bool
    }))
  }))
});

const PowerSchoolStudentType = PropTypes.shape({
  id: PropTypes.number,
  '@extensions': PropTypes.arrayOf(PropTypes.string),
  '@expansions': PropTypes.arrayOf(PropTypes.string),
  name: PropTypes.shape({
    first_name: PropTypes.string,
    middle_name: PropTypes.string,
    last_name: PropTypes.string
  }),
  _extension_data: PropTypes.shape({
    _table_extension: PropTypes.arrayOf(PropTypes.object)
  }),
  contacts: PropTypes.arrayOf(PowerSchoolContactType)
});

const PowerSchoolContact = ({ contact }) => (
  <div>
    <h4>
      {`${contact.firstName || ''} ${contact.middleName || ''} ${contact.lastName || ''}`}
      &nbsp;
      <small>
        {`(${contact.contactId})`}
      </small>
    </h4>
    <br />
    <h5>Addresses</h5>
    <table className='table'>
      <tbody>
        {contact.addresses.map(address => (
          <tr key={address.contactsAddressId}>
            <td>{address.addressType}</td>
            <td>
              {address.street}
              <br />
              {address.linetwo && (
                <span>
                  {address.linetwo}
                  <br />
                </span>
              )}
              {address.unit && (
                <span>
                  {address.unit}
                  <br />
                </span>
              )}
              {`${address.city}, ${address.state} ${address.postalcode}`}
              {address.country}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <h5>Phone numbers</h5>
    <table className='table'>
      <tbody>
        {contact.phones.map(phone => (
          <tr key={phone.contactsPhoneId}>
            <td>{phone.phoneType}</td>
            <td>{phone.phoneNumber}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <h5>Emails</h5>
    <table className='table'>
      <tbody>
        {contact.emails.map(email => (
          <tr key={email.contactEmailId}>
            <td>{email.type}</td>
            <td>{email.address}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <h5>Contact Students</h5>
    <table className='table'>
      <tbody>
        {contact.contactStudents.map(contactStudent => (
          <React.Fragment key={contactStudent.studentContactId}>
            <tr>
              <td>DCID (PowerSchool ID)</td>
              <td>{contactStudent.dcid}</td>
            </tr>
            <tr>
              <td>Original Contact Type</td>
              <td>{contactStudent.originalContactType}</td>
            </tr>
            <tr>
              <td>Details</td>
              <td>
                {contactStudent.studentDetails.map(detail => (
                  <div className='studentdetail' key={detail.studentContactDetailId}>
                    <div>
                      <span className='studentdetail__name'>Relationship</span>
                      <span className='studentdetail__value'>{detail.relationship}</span>
                    </div>
                    <div>
                      <span className='studentdetail__name'>Custodial</span>
                      <span className='studentdetail__value'>
                        <FA icon={detail.custodial ? faCheckSquare : faSquare} />
                      </span>
                    </div>
                    <div>
                      <span className='studentdetail__name'>Emergency</span>
                      <span className='studentdetail__value'>
                        <FA icon={detail.emergency ? faCheckSquare : faSquare} />
                      </span>
                    </div>
                    <div>
                      <span className='studentdetail__name'>Lives With</span>
                      <span className='studentdetail__value'>
                        <FA icon={detail.livesWith ? faCheckSquare : faSquare} />
                      </span>
                    </div>
                    <div>
                      <span className='studentdetail__name'>School Pickup</span>
                      <span className='studentdetail__value'>
                        <FA icon={detail.schoolPickup ? faCheckSquare : faSquare} />
                      </span>
                    </div>
                    <div>
                      <span className='studentdetail__name'>Receives Mail</span>
                      <span className='studentdetail__value'>
                        <FA icon={detail.receivesMail ? faCheckSquare : faSquare} />
                      </span>
                    </div>
                  </div>
                ))}
              </td>
            </tr>
          </React.Fragment>
        ))}
      </tbody>
    </table>
  </div>
);

PowerSchoolContact.propTypes = {
  contact: PowerSchoolContactType.isRequired
};

const ExpansionData = ({ expansion, level }) => (
  Object.keys(expansion).map(key => (
    <div key={key} className={level > 0 ? 'expansion__level' : 'expansion'}>
      <span className='expansion__name'>{key}</span>
      {
        typeof expansion[key] === 'object'
          ? <ExpansionData expansion={expansion[key]} level={level + 1} />
          : <span className='expansion__value'>{expansion[key]}</span>
      }
    </div>
  ))
);

ExpansionData.propTypes = {
  expansion: PropTypes.shape({}).isRequired,
  level: PropTypes.number
};

ExpansionData.defaultProps = {
  level: 0
};

const ExtensionField = ({ field }) => (
  <div className='extension'>
    <span className='extension__name'>{field.name}</span>
    <span className='extension__value'>{field.value}</span>
  </div>
);

ExtensionField.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }).isRequired
};

const PowerSchoolStudent = ({ student }) => {
  const [showRaw, setShowRaw] = useState(false);

  return (
    <div>
      <h2>
        {`${student.name.first_name || ''} ${student.name.middle_name || ''} ${student.name.last_name || ''}`}
        &nbsp;
        <LinkButton content='Show Raw JSON' onClick={() => setShowRaw(!showRaw)} />
      </h2>
      <pre style={{ display: showRaw ? '' : 'none' }}>{JSON.stringify(student)}</pre>
      <div className='expansions'>
        <h3>Expansions</h3>
        <table className='table table-bordered'>
          <tbody>
            {student['@expansions'].map(exp => (
              <tr key={exp}>
                <td>{exp}</td>
                <td>
                  {student[exp] && <ExpansionData expansion={student[exp]} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className='extensions'>
        <h3>Extensions</h3>
        <table className='table table-bordered'>
          <tbody>
            {
              /* eslint-disable-next-line no-underscore-dangle */
              student._extension_data._table_extension.map(ext => (
                <tr key={ext.name}>
                  <td>{ext.name}</td>
                  <td>
                    {
                      /* eslint-disable-next-line no-underscore-dangle */
                      Array.isArray(ext._field)
                        /* eslint-disable-next-line no-underscore-dangle */
                        ? (ext._field.map(field => <ExtensionField key={`${ext.name}__${field.name}`} field={field} />))
                        /* eslint-disable-next-line no-underscore-dangle */
                        : <ExtensionField field={ext._field} />
                    }
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
      <div className='contacts'>
        <h3>Contacts</h3>
        {student.contacts.map(contact => <PowerSchoolContact key={contact.contactId} contact={contact} />)}
      </div>
    </div>
  );
};

PowerSchoolStudent.propTypes = {
  student: PowerSchoolStudentType.isRequired
};

const PowerSchoolContacts = (props) => {
  const {
    duckMeta,
    student,
    router,
    params: { studentId }
  } = props;

  const [powerSchoolId, setPowerSchoolId] = useState(studentId);

  const handleSearch = () => {
    if (powerSchoolId !== studentId) {
      router.push(`/internal/power_school_contacts/${powerSchoolId}`);
    }
  };

  return (
    <div className='power-school-contacts-debug'>
      <h2>PowerSchool Contacts Debug</h2>
      <form onSubmit={() => handleSearch()}>
        <div className='row'>
          <div className='col-5'>
            <div className='input-group input-group-sm'>
              <input
                type='text'
                className='form-control w-25'
                placeholder='PowerSchool ID'
                defaultValue={powerSchoolId}
                onChange={(e) => setPowerSchoolId(e.currentTarget.value)}
              />
              <button
                className='btn btn-primary'
                disabled={!powerSchoolId}
                type='submit'
              >
                <FA icon={faSearch} />
                <span className='visually-hidden'>Search</span>
              </button>
            </div>
          </div>
        </div>
      </form>
      <br />
      <Placeholder.text
        ready={!duckMeta.student.fetching}
        width={400}
        content={() => {
          if (duckMeta.student.error) {
            return (
              <div className='alert alert-danger'>
                Error loading PowerSchool Contact. Ensure the provided PowerSchool ID is correct.
              </div>
            );
          } else if (duckMeta.student.ready) {
            return <PowerSchoolStudent student={student} />;
          }
          return null;
        }}
      />
    </div>
  );
};

PowerSchoolContacts.propTypes = {
  duckMeta: PropTypes.shape({
    student: PropTypes.shape({
      error: PropTypes.bool,
      fetching: PropTypes.bool,
      ready: PropTypes.bool
    })
  }),
  student: PowerSchoolStudentType.isRequired,
  router: PropTypes.shape({
    push: PropTypes.func
  }),
  params: PropTypes.shape({ studentId: PropTypes.string })
};
PowerSchoolContacts.defaultProps = {
  duckMeta: {
    student: {
      error: false,
      fetching: false,
      ready: false
    }
  },
  router: {
    push: () => false
  },
  params: {}
};

const mapDucksToProps = (props) => {
  const { params: { studentId } } = props;

  return {
    student: studentId ? singleDuck(powerSchoolContacts, studentId) : null
  };
};

const mapStateToProps = () => ({});
const mapDispatchToProps = () => ({});

export default withRouter(duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps)(PowerSchoolContacts));
